import type React from 'react';
import { useCallback, useState } from 'react';
import {
  autoUpdate,
  flip,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { setupCloseOnContentClick } from './useDropdown';

function useContextMenu(): {
  hideMenu: () => void;
  isVisible: boolean;
  onContextMenu: (e: React.MouseEvent) => void;
  setMenuRef: (node: HTMLElement | null) => void;
  styles: { zIndex: number };
  getButtonProps: (
    extra: React.HTMLAttributes<Element>,
  ) => React.HTMLAttributes<Element>;
} {
  const [isVisible, setVisibility] = useState<boolean>(false);

  const { context, refs, strategy, x, y } = useFloating({
    open: isVisible,
    onOpenChange: setVisibility,
    placement: 'bottom-start',
    whileElementsMounted: (reference, floating, update) => {
      // Remember to cleanup everything happening inside this!
      const cleanUpAutoUpdate = autoUpdate(reference, floating, update);

      const cleanUpCloseOnContentClick = setupCloseOnContentClick(
        floating,
        true,
        setVisibility,
      );

      return () => {
        cleanUpAutoUpdate();
        cleanUpCloseOnContentClick();
      };
    },
    middleware: [flip(), shift()],
  });

  const styles = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    zIndex: 1000,
  };

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useDismiss(context),
  ]);

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const { clientX, clientY } = e;

      refs.setReference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: clientX,
            y: clientY,
            top: clientY,
            left: clientX,
            right: clientX,
            bottom: clientY,
          };
        },
      });

      setVisibility(true);
    },
    [refs],
  );

  const hideMenu = useCallback(() => {
    setVisibility(false);
  }, []);

  /** Apply these props to any element that should toggle the context menu.
   * Use the onPointerDown event if you need to execute anything before the context menu is opened.
   * Use like:
    <div
      {...getItemMenuButtonProps({
        onPointerDown: () => {
          // whatever else you need to do, context menu toggling is handled automatically
        },
      })}
    />
  */
  const getButtonProps = useCallback(
    (extra: React.HTMLAttributes<Element>) =>
      getReferenceProps({
        ...extra,
        onPointerDown: (e) => {
          refs.setReference(e.currentTarget);
          extra.onPointerDown?.(e);
          setVisibility(!isVisible);
        },
      }),
    [getReferenceProps, refs, setVisibility, isVisible],
  );

  return {
    hideMenu,
    isVisible,
    onContextMenu,
    setMenuRef: refs.setFloating,
    styles,
    getButtonProps,
  };
}

export default useContextMenu;
